

















import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import PropertyForm from "@/components/property/PropertyForm.vue";
import { PropertyFormModel } from "@/components/property/types";
import { NavigationGuardNext, Route } from "vue-router";
import GlobalFilterSensitiveMixin from "@/mixins/GlobalFilterSensitiveMixin";
import { Action, Getter } from "vuex-class";
import { saveFile } from "@/shared/saveFile";
import { OwnerPricingModelEnum } from "@/shared/models/enums.type";

@Component({
  components: {
    PropertyForm,
  },
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext) {
    const fresh = to.params.id;
    if (to.name === "New property") {
      next((vm: Vue) => {
        (vm as Property).model = new PropertyFormModel();
        (vm as Property).model.ownerPricingModelId = OwnerPricingModelEnum.AbsoluteSame;
      });
    } else {
      Vue.axios
        .get(`properties/${fresh}`)
        .then(({ data }) => {
          next(
            // prettier-ignore
            (vm: Vue) => {
              const that = vm as Property;
              that.model = Object.assign(new PropertyFormModel(), data);
              that.breadcrumbs = [that.$t("Edit property").toString(), data.name];
              that.setCustomBreadcrumbs && that.setCustomBreadcrumbs(that.breadcrumbs);
            }
          );
        })
        .catch(() => next({ name: "Properties and units", replace: true }));
    }
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
    const self = this as any;
    if (to.name === "Edit property") {
      next();
    } else {
      const noChanges = self.propertyForm.checkForChanges();
      if (noChanges) {
        next();
      } else {
        this.$store.dispatch("showNotification", {
          color: "warning",
          message: this.$t(
            "Changes pending. Are you sure you want to close the form?"
          ),
          actions: [
            {
              text: this.$t("Yes"),
              fun: () => {
                next();
                return true;
              },
            },
            {
              text: this.$t("No"),
              fun: () => {
                next(false);
                return true;
              },
            },
          ],
          timeout: 5000,
        });
      }
    }
  },
  mixins: [GlobalFilterSensitiveMixin],
})
export default class Property extends Vue {
  get id() {
    return this.$route.name === "New property" ? 0 : +this.$route.params.id;
  }

  @Action("setCustomBreadcrumbs") setCustomBreadcrumbs?: (
    payload: string[] | undefined
  ) => Promise<void>;
  @Getter("customBreadcrumbs") customBreadcrumbs?: string[];

  async destroyed() {
    if (this.customBreadcrumbs == this.breadcrumbs) {
      this.setCustomBreadcrumbs && this.setCustomBreadcrumbs(undefined);
    }
  }

  @Ref("propertyForm") propertyForm?: {
    resetFormModel: () => void;
  };

  model = new PropertyFormModel();

  breadcrumbs: string[] = [];

  get tab() {
    const tab = this.$route.query.tab;
    return tab;
  }

  set tab(fresh: string | (string | null)[]) {
    this.$router.replace({
      path: this.$route.path,
      params: this.$route.params,
      query: { tab: fresh },
      replace: true,
    });
  }

  // prettier-ignore
  @Action("globalFilters/refreshFilter") refreshFilter?: ( replace?: boolean ) => void;
  // prettier-ignore
  @Action("businessUnit/refreshBusinessUnits") refreshBusinessUnits?: () => void;

  submitting = false;
  submit(
    model: PropertyFormModel,
    ownerContractExportType?: number,
  ) {
    this.submitting = true;

    this.model.categorizationDate =
      this.model.categorizationDate?.toString() === ""
        ? undefined
        : this.model.categorizationDate;

    const notifySuccess = () => {
      this.$store.dispatch("showNotification", {
        color: "success",
        message: this.$t("Property.SaveSuccess"),
        timeout: 5000,
      });
    };

    if (this.id === 0) {
      this.axios
        .post<number>("/properties", model)
        .then(({ data }) => {
          this.$router.replace({
            name: "Edit property",
            params: { id: data.toString() },
            replace: true,
          });

          if (this.refreshFilter) {
            this.refreshFilter();
          }
          if (this.refreshBusinessUnits) {
            this.refreshBusinessUnits();
          }
        })
        .then(notifySuccess)
        .then(() => this.propertyForm?.resetFormModel())
        .finally(() => (this.submitting = false));
    } else {
      this.axios
        .put(`/properties/${this.id}`, model)
        .then(() => {
          if (this.refreshFilter) {
            this.refreshFilter();
          }
          if (this.refreshBusinessUnits) {
            this.refreshBusinessUnits();
          }
          if (ownerContractExportType) {
            this.generateOwnerContract(ownerContractExportType);
          }
        })
        .then(notifySuccess)
        .then(() => this.propertyForm?.resetFormModel())
        .finally(() => (this.submitting = false));
    }
  }

  generateOwnerContract(ownerContractExportType: number) {
    this.axios
      .get(`/properties/generateOwnerContract`, {
        params: {
          propertyId: this.model.id,
          exportType: ownerContractExportType,
        },
        responseType: "blob",
      })
      .then((res) => {
        const groups = /filename="?(?<filename>[^;"]+)"?;/.exec(
          res.headers["content-disposition"]
        )?.groups;
        saveFile(res.data, groups?.filename ?? "OwnerContract.docx");
        this.$store.dispatch("showNotification", {
          color: "success",
          message: this.$t("GenerateOwnerContractSuccess"),
          timeout: 5000,
        });
      });
  }

  @Watch("sensitiveKey")
  tenantChanged(fresh: string, old: string) {
    const freshTenantParts = fresh?.split("_");
    const oldTenantParts = old?.split("_");

    const [freshTenant, oldTenant] = [
      freshTenantParts && freshTenantParts.length > 0
        ? freshTenantParts[0]
        : "",
      oldTenantParts && oldTenantParts.length > 0 ? oldTenantParts[0] : "",
    ];

    if (freshTenant !== oldTenant) {
      this.$router.push("Properties and units");
    }
  }
  async serviceAdded() {
    await Vue.axios.get(`properties/${this.model.id}`).then((res) => {
      this.$store.dispatch("showNotification", {
        color: "success",
        message: this.$t("Property.AddOnService.AddSuccess"),
        timeout: 5000,
      });
      this.model = res.data;
    });
  }
}
